import React from 'react';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import { LiveEventSelect } from 'components/live-event-select';
import gtm from 'general/js/gtm';
import {
    getRoundById,
    isRoundChoicesEqual,
    isRoundChoiceAvailable,
    setSelectedChoice,
} from 'general/js/helpers';
import { LiveHardestHole } from 'components/live-hardest-hole';
import { LiveAveragePar } from 'components/live-average-par';
import { LiveStatsCardContainer, statsNames } from 'components/live-stats';
import { getHardestHole } from './enums';

class LiveHoleStats extends React.Component {
    constructor(props) {
        super(props);
        this.statsNames = [
            statsNames.EASIEST_HOLES,
            statsNames.HARDEST_HOLES,
        ];
        this.state = {
            roundChoices: null,
            selectedRoundId: null,
            hasUserSelected: false,
            showEasiestHoleStat: false,
            showHardestHoleStat: false
        };
    }

    componentDidMount() {
        this.updateChoices();
        this.setHoleStats();
    }

    componentDidUpdate() {
        this.updateChoices();
    }

    getScoringRound(id) {
        const { scoringStats } = this.props;

        if (!scoringStats) return null;

        return getRoundById(scoringStats.rounds, id);
    }

    getAvailableChoices() {
        const { liveScanner, scoringStats, activeHole } = this.props;
        const roundChoices = get(liveScanner, 'liveComponents.stats.roundChoices');

        if (!Array.isArray(scoringStats.rounds)) return [];

        return cloneDeep(roundChoices).filter((choice) => {
            const roundStats = this.getScoringRound(choice.value);

            if (!roundStats) return false;

            return this.statsNames.some((statsName) => {
                if (!roundStats[statsName] ||
                    !Array.isArray(roundStats[statsName].stats)
                ) {
                    return false;
                }

                return roundStats[statsName].stats.some((statsItem) =>
                    statsItem.holeId.toString() === activeHole.id.toString());
            });
        });
    }

    updateChoices() {
        const { liveScanner, scoringStats } = this.props;
        const { selectedRoundId } = this.state;

        if (!liveScanner || !scoringStats) return;

        const prevChoices = this.state.roundChoices;
        const roundChoices = this.getAvailableChoices();

        if (this.state.hasUserSelected &&
            isRoundChoiceAvailable(roundChoices, selectedRoundId)
        ) {
            setSelectedChoice(roundChoices, selectedRoundId);

            if (!isRoundChoicesEqual(prevChoices, roundChoices)) {
                this.setState({ roundChoices });
            }
            return;
        }

        const foundChoice = roundChoices.find((choice) => choice.selected === true);

        if (!foundChoice && roundChoices.length) {
            const lastChoice = roundChoices[roundChoices.length - 1];

            lastChoice.selected = true;
            this.setState({
                roundChoices,
                selectedRoundId: lastChoice.value,
                hasUserSelected: true,
            });
            return;
        }

        if (isRoundChoicesEqual(prevChoices, roundChoices)) return;

        this.setState({
            roundChoices,
            selectedRoundId: (foundChoice && foundChoice.value) || null,
            hasUserSelected: false,
        });
    }

    setHoleStats() {
        const { liveScanner } = this.props;

        if (liveScanner !== undefined) {
            const course = get(liveScanner, 'liveComponents.course');
            this.setState({ showEasiestHoleStat: course.showEasiestHoleStat, showHardestHoleStat: course.showHardestHoleStat });
        }
    }

    setRound = (roundId) => {
        gtm.push('Hole Card', `Round Stats > ${roundId}`, this.props.activeHole.id);
        this.setState({ selectedRoundId: roundId });
    }

    getCard(statsName) {
        const { scoringStats, activeHole } = this.props;
        const scoringRound = this.getScoringRound(this.state.selectedRoundId);

        if (!scoringRound) return null;

        const sitecoreData = get(scoringStats, `sitecoreData[${statsName}]`) || null;
        const stats = get(scoringRound, `[${statsName}].stats`) || [];
        const average = get(scoringRound, `[${statsName}].average`);

        if (!sitecoreData) return null;

        const statsCard = {
            name: statsName,
            stats: [],
            sitecoreData
        };

        const holeStats = stats.find((hole) =>
            hole.holeId.toString() === activeHole.id.toString()) || null;

        if (holeStats) {
            statsCard.stats.push(holeStats);
        }

        if (typeof average === 'number') {
            statsCard.stats.push({
                statsId: 'Average',
                value: average,
                text: 'Course average',
                isSingle: true
            });
        }

        if (!statsCard.stats.length) return null;

        return statsCard;
    }

    render() {
        const { scoringStats, activeHole } = this.props;
        const { roundChoices, selectedRoundId, showEasiestHoleStat, showHardestHoleStat } = this.state;

        if (!activeHole ||
            !scoringStats ||
            !scoringStats.sitecoreData ||
            !roundChoices ||
            !roundChoices.length
        ) {
            return null;
        }

        const hardestTitle = getHardestHole(
            this.getScoringRound(selectedRoundId),
            activeHole.id
        );

        return (
            <div className="live-hole-stats">
                <div className="live-hole-stats__header">
                    {
                        roundChoices.length === 1 &&
                        <div className="single-round">{roundChoices[0].label}</div>
                    }
                    {
                        roundChoices.length > 1 &&
                        <div className="live-hole-stats__dropdown">
                            <LiveEventSelect
                                choices={roundChoices}
                                onChange={this.setRound}
                            />
                        </div>
                    }
                </div>
                <div className="live-hole-stats__list">
                    <div className="live-hole-stats__wrapper">
                        <div className="live-hole-stats__hardest-hole">
                            <LiveHardestHole
                                title={hardestTitle}
                                hole={activeHole}
                                currentRound={selectedRoundId}
                            />
                        </div>
                        <div className="live-hole-stats__average-par">
                            <LiveAveragePar
                                hole={activeHole}
                                currentRound={selectedRoundId}
                            />
                        </div>
                    </div>
                    {
                        showEasiestHoleStat &&
                        <div className="live-hole-stats__card">
                            <LiveStatsCardContainer
                                model={this.getCard(statsNames.EASIEST_HOLES)}
                                isInteractive={false}
                            />
                        </div>
                    }
                    {
                        showHardestHoleStat &&
                        <div className="live-hole-stats__card">
                            <LiveStatsCardContainer
                                model={this.getCard(statsNames.HARDEST_HOLES)}
                                isInteractive={false}
                            />
                        </div>
                    }
                </div>
            </div>
        );
    }
}

export default LiveHoleStats;
